import "../styles/globals.scss"
import { ParsedUrlQuery } from "querystring"
import React, { ReactElement } from "react"
import App, { AppContext, AppProps } from "next/app"
import Modal from "react-modal"
import { NextRouter, Router } from "next/router"
import { inspect } from "@xstate/inspect"
import * as F from "fp-ts/function"
import * as TE from "fp-ts/TaskEither"
import * as O from "fp-ts/Option"
import * as TO from "fp-ts/TaskOption"
import * as E from "fp-ts/Either"
import {
    CruiseNotEnabled,
    GeoLocationResponse,
    IsEnabledResponse,
    IsEnabledResponseType,
} from "@ssc-booking/api-contracts"
import { CruiseCode, decode } from "@ssc-booking/functional-support"
import * as GenericError from "@ssc-booking/functional-support/src/generic-error"
import Head from "next/head"
import svgSprite from "@silversea-ssc/design-system-icons/dist/svg-sprite.svg"
import { defaultGeoLocation, Geolocation } from "../app-core/_common/providers/geolocation-provider"
import { useSetupRollbar } from "../app-core/_common/rollbar.setup.client"
import { getFromQuery } from "../app-core/_common/routing"
import { Locale } from "../app-core/_common/format/localization"
import { countryFromContext } from "../app-core/_common/country"
import { getJson } from "../app-core/_common/http"
import { WebAppConfig } from "../app-core/_common/web-app-config"
import { I18nCatalog } from "../app-core/_common/providers/lingui-provider"
import { CoreProviders } from "../app-core/_common/providers/core-providers"
import { startWebServerStub } from "../playwright/support/websocket-server"
import { assignActiveExperiments } from "../app-core/_common/ab-testing/assign/handler"
import { ActiveExperimentTags } from "../app-core/_common/ab-testing/client/ExperimentTagProvider"
import { generateLivePersonEmbedJS } from "../app-core/_common/chat.init"
import CustomAppSafe, { SafePageProps } from "./_app-safe"
import Page404 from "./404"
import config from "./_app-config"

if (
    process.env.NODE_ENV === "development" &&
    global.window != undefined &&
    global.window.location.search?.includes("inspect")
) {
    inspect({
        iframe: false,
    })
}

Modal.setAppElement("#__next")

type Props = AppProps<SafePageProps> & {
    geoLocation: Geolocation
    cruiseEnabled: IsEnabledResponse["type"]
    config: WebAppConfig
    i18nCatalog: I18nCatalog
    activeExperimentTags: ActiveExperimentTags
}

const CustomApp = ({
    Component,
    pageProps,
    router,
    geoLocation,
    cruiseEnabled,
    config,
    i18nCatalog,
    activeExperimentTags,
}: Props): ReactElement => {
    startWebServerStub()
    useSetupRollbar(config)

    const cruiseCode = F.pipe(
        getFromQuery(router, "cruiseCode"),
        E.fromNullable("Missing cruise code"),
        E.chainW(decode(CruiseCode.strictType)),
        E.getOrElseW(F.constUndefined),
    )
    const locale = localeFrom(router)

    if (isPaymentResponse(router) || isTestDeckPlanPage(router))
        return (
            <CoreProviders locale={locale} geoLocation={geoLocation} config={config} i18nCatalog={i18nCatalog}>
                <Component {...pageProps} />
            </CoreProviders>
        )

    if (isCruise(router) && cruiseEnabled == "CruiseNotEnabled")
        return (
            <CoreProviders locale={locale} geoLocation={geoLocation} config={config} i18nCatalog={i18nCatalog}>
                <Page404 />
            </CoreProviders>
        )

    if (isCruise(router) && cruiseCode && cruiseEnabled != "CruiseNotEnabled") {
        return (
            <React.Fragment>
                <Head>
                    <link
                        key="icon-font"
                        rel="preload"
                        as="image"
                        type="image/svg+xml"
                        href={svgSprite}
                        crossOrigin="anonymous"
                    />
                    <script
                        type="text/javascript"
                        dangerouslySetInnerHTML={{
                            __html: generateLivePersonEmbedJS(config.livePerson.accountId, geoLocation.countryCode),
                        }}
                    />
                </Head>
                <CustomAppSafe
                    pageProps={pageProps}
                    Component={Component}
                    router={router}
                    geoLocation={geoLocation}
                    cruiseCode={cruiseCode}
                    locale={locale}
                    cruiseEnabled={cruiseEnabled}
                    config={config}
                    i18nCatalog={i18nCatalog}
                    activeExperimentTags={activeExperimentTags}
                />
            </React.Fragment>
        )
    }

    return (
        <CoreProviders locale={locale} geoLocation={geoLocation} config={config} i18nCatalog={i18nCatalog}>
            <Component {...pageProps} />
        </CoreProviders>
    )
}

CustomApp.getInitialProps = async (appContext: AppContext) => {
    const activeExperiments = assignActiveExperiments(appContext.ctx)

    // TODO: this code is also called on the client and config() access
    // the process.env.STAGE which is only available on the server.
    // This only become a problem if there's a 500 server-side.
    // Take a look at:
    // env: { STAGE: process.env.STAGE } in next.config.js

    const countryCode = countryFromContext(appContext.ctx)
    const cruiseCode = getFromQuery(appContext.router, "cruiseCode")
    const locale = localeFrom(appContext.router)
    const _config = config()

    const [appProps, _geoLocation, cruiseEnabled, i18nCatalog] = await Promise.all([
        App.getInitialProps(appContext),
        geoLocation(_config, countryCode)(),
        isEnabled(appContext.ctx.query, _config, cruiseCode, countryCode)(),
        import(`../locales/${locale}/messages`),
    ])

    return {
        ...appProps,
        geoLocation: _geoLocation,
        cruiseEnabled,
        config: _config,
        i18nCatalog,
        activeExperimentTags: activeExperiments,
    }
}

const geoLocation = (config: WebAppConfig, countryCode: string) =>
    F.pipe(
        getJson(`${config.serverUrl}geoLocation?country=${countryCode}`),
        TE.chainEitherK(decode(GeoLocationResponse.strictType)),
        TE.match(toDefaultGeoLocation, toGeoLocation),
    )

const toGeoLocation = (response: GeoLocationResponse): Geolocation => ({
    countryCode: response.countryCode,
    market: response.market,
    unit: response.unit,
    defaultCurrency: response.defaultCurrency,
    phone: F.pipe(response.phone, O.toUndefined),
    countryPath: response.countryPath,
})
const toDefaultGeoLocation = (e: GenericError.GenericError) => {
    console.error("Error fetching geo location", e)
    return defaultGeoLocation
}
const isEnabled = (query: ParsedUrlQuery, config: WebAppConfig, cruiseCode: string | undefined, countryCode: string) =>
    F.pipe(
        isEnabledFromQuery(query),
        TO.getOrElse(() => isEnabledFromServer(config, cruiseCode, countryCode)),
    )

const isEnabledFromQuery = (query: ParsedUrlQuery) => {
    const internalFlow = F.pipe(IsEnabledResponseType.strictType.decode(query.__flow__), TO.fromEither)
    const publicFlow = F.pipe(IsEnabledResponseType.strictType.decode(query.flow), TO.fromEither)
    return TO.alt(() => publicFlow)(internalFlow)
}

const isEnabledFromServer = (config: WebAppConfig, cruiseCode: string | undefined, countryCode: string) =>
    F.pipe(
        cruiseCode,
        TE.fromNullable(GenericError.fatal("Missing cruise code")),
        TE.chain((cruiseCode) => getJson(`${config.serverUrl}${cruiseCode}/isEnabled?country=${countryCode}`)),
        TE.chainEitherK(decode(IsEnabledResponse.strictType)),
        TE.match(F.constant(CruiseNotEnabled), IsEnabledResponse.lensFromProp("type").get),
    )

const isCruise = (router: NextRouter): boolean =>
    router.pathname.includes("/v3/[cruiseCode]") || router.pathname.includes("/v4/[cruiseCode]")
const isPaymentResponse = (router: NextRouter): boolean =>
    router.pathname.includes("payment-response") || router.pathname.includes("demetra-response")
const isTestDeckPlanPage = (router: NextRouter): boolean => router.pathname.includes("/test-deck-plans")
const localeFrom = (router: Router) => (router.locale || "en") as Locale

// eslint-disable-next-line import/no-unused-modules
export default CustomApp
